import { useLife } from '@/state/life';
import { useMarkets } from '@/state/markets';
import { useEmail } from '@/state/email';
import { usePhone } from '@/state/phone';
import { useNotifications } from '@/state/notifications';
import { useCinematic } from '@/components/CinematicTakeover';
import { useMeta } from '@/state/meta';
import { sounds } from '@/audio/synth';
import { PHONE_ACTION_HANDLERS } from './phoneActions';

/**
 * Tax Season arc.
 *
 * Triggered as a phone offer once per "year" (real-time gated). 4
 * filing-strategy decisions decide your effective tax rate (cash hit)
 * and IRS heat exposure. Aggressive structuring gives more savings now
 * but bigger heat later.
 */

interface Beat {
  text: string;
  options: { label: string; effect: () => void }[];
}

const BEATS: Beat[] = [
  {
    text: 'YOUR ACCOUNTANT: "we have 3 strategies. how aggressive do you want to be?"',
    options: [
      { label: 'pay full tax. boring. clean.', effect: () => {
        const cash = useMarkets.getState().cash;
        const tax = Math.max(0, Math.floor(cash * 0.05));
        useMarkets.getState().addCash(-tax);
        useLife.getState().bumpIrsHeat(-15);
      } },
      { label: 'standard structuring. normal billionaire stuff.', effect: () => {
        const cash = useMarkets.getState().cash;
        const tax = Math.max(0, Math.floor(cash * 0.025));
        useMarkets.getState().addCash(-tax);
        useLife.getState().bumpIrsHeat(2);
      } },
      { label: 'aggressive structuring. flag-on-the-form-but-it-clears.', effect: () => {
        const cash = useMarkets.getState().cash;
        const tax = Math.max(0, Math.floor(cash * 0.005));
        useMarkets.getState().addCash(-tax);
        useLife.getState().bumpIrsHeat(15);
      } },
    ],
  },
  {
    text: 'CHARITY DEDUCTION question. you donated \$0 this year. how do we frame it?',
    options: [
      { label: 'don\'t claim any. take the L.', effect: () => {
        useLife.getState().bumpIrsHeat(-3);
      } },
      { label: '"\$50K to my own foundation."', effect: () => {
        useLife.getState().bumpIrsHeat(8);
      } },
      { label: '"\$200K of \"art\" which is actually a NFT i made myself."', effect: () => {
        useLife.getState().bumpIrsHeat(20);
        useMarkets.getState().addCash(50_000);
      } },
    ],
  },
  {
    text: 'CRYPTO REPORTING question. you traded a lot. how truthful are we being?',
    options: [
      { label: 'fully report. it\'s a paper trail anyway.', effect: () => {
        useLife.getState().bumpIrsHeat(-8);
      } },
      { label: '"forgot the wallet password."', effect: () => {
        useLife.getState().bumpIrsHeat(20);
        useLife.getState().bumpFbiHeat(8);
      } },
      { label: 'route it through 4 exchanges and call it "settlement variance"', effect: () => {
        useLife.getState().bumpIrsHeat(15);
        useMarkets.getState().addCash(40_000);
      } },
    ],
  },
  {
    text: 'FILING DEADLINE in 14 minutes. final move?',
    options: [
      { label: 'file on time. exhale.', effect: () => {
        useLife.getState().bumpSanity(4);
      } },
      { label: 'request an extension via fax at the last minute', effect: () => {
        useLife.getState().bumpIrsHeat(5);
      } },
      { label: '"forget" to file. "they\'ll let me know."', effect: () => {
        useLife.getState().bumpIrsHeat(35);
        useLife.getState().bumpSanity(-6);
      } },
    ],
  },
];

export function startTaxSeasonArc(): void {
  useMeta.getState().recordLore('arc-tax-season', 'Tax Season');
  useCinematic.getState().fire({
    id: `taxes-${Date.now()}`,
    type: 'breaking',
    headline: 'TAX SEASON.',
    subhead: 'your accountant is on the phone. she has 3 strategies. one is legal. two are "interpretively legal." you have 4 decisions.',
    durationMs: 5500,
  });
  sounds.notifyAlert();

  for (let i = 0; i < BEATS.length; i++) {
    const b = BEATS[i]!;
    setTimeout(() => {
      const ids = ['a', 'b', 'c'];
      const actions = b.options.map((opt, idx) => ({
        id: ids[idx]!,
        label: opt.label,
        effectId: `taxes-q${i}-${ids[idx]}`,
      }));
      for (let j = 0; j < b.options.length; j++) {
        const id = `taxes-q${i}-${ids[j]}`;
        const opt = b.options[j]!;
        PHONE_ACTION_HANDLERS[id] = () => {
          opt.effect();
          sounds.fill();
        };
      }
      usePhone.getState().receiveMessage({
        threadId: 'lawyer',
        fromNpc: 'lawyer',
        text: b.text,
        actions,
      });
      useNotifications.getState().push({ surfaceId: 'phone', severity: 'urgent', text: `taxes: decision ${i + 1}/4` });
      sounds.notifyAlert();
    }, 5_000 + i * 25_000);
  }

  setTimeout(() => {
    useEmail.getState().receive({
      fromNpc: 'irs',
      fromAddress: 'no-reply@irs.gov',
      subject: 'Filing receipt — Form 1040',
      body: "Thank you for filing.\n\nYour return has been received and queued for review.\n\nNo action required at this time.\n\nWe will be in touch if your structuring choices warrant follow-up. Have a nice year.\n\n— Internal Revenue Service",
    });
    useNotifications.getState().push({ surfaceId: 'email', severity: 'info', text: 'IRS: filing receipt' });
  }, 5_000 + BEATS.length * 25_000 + 5_000);
}
